import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { MaskedLines } from "@/components/Reveal";
import { useQuote } from "@/components/QuoteDialog";

const fade = (d) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay: d, ease: [0.22, 1, 0.36, 1] },
});

export const Hero = ({ base = 0 }) => {
  const ref = useRef(null);
  const { openQuote } = useQuote();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 110, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 110, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-10%"]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => { mx.set(0); my.set(0); }}
      className="relative min-h-[100svh] overflow-hidden bg-[#141412] text-[#FAF8F5] grain"
      data-testid="hero-section"
    >
      <div className="pointer-events-none absolute -right-40 top-10 h-[620px] w-[620px] rounded-full bg-[#C59B27]/20 blur-[140px]" />
      <div className="relative mx-auto grid max-w-[1400px] items-center gap-12 px-5 pb-20 pt-32 sm:px-8 lg:min-h-[100svh] lg:grid-cols-12 lg:pt-28">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p {...fade(base)} className="eyebrow flex items-center gap-3 text-[#C59B27]">
            <span className="h-px w-10 bg-[#C59B27]" /> Contract catering · United Kingdom
          </motion.p>
          <MaskedLines
            onMount
            delay={base + 0.1}
            lines={["Kitchens", "that care", "for Britain."]}
            italic={1}
            className="mt-6 font-display text-[3.4rem] font-bold leading-[0.95] tracking-[-0.03em] sm:text-7xl lg:text-[7.4rem]"
            testId="hero-heading"
          />
          <motion.p {...fade(base + 0.7)} className="mt-8 max-w-xl text-base leading-relaxed text-stone-300 sm:text-lg">
            Culifect runs fresh, reliable catering for care homes, schools and workplaces across the UK. Choose a plan and subscribe today, or ask us for a tailored quote.
          </motion.p>
          <motion.div {...fade(base + 0.85)} className="mt-10 flex flex-wrap gap-3">
            <button onClick={() => openQuote()} className="btn-gold" data-testid="hero-quote-button">
              Get a contract quote <ArrowUpRight className="h-4 w-4" />
            </button>
            <a href="#sectors" className="btn-ghost text-[#FAF8F5] hover:bg-white/10" data-testid="hero-plans-link">
              See plans <ArrowDownRight className="h-4 w-4" />
            </a>
          </motion.div>
          <motion.div {...fade(base + 1)} className="mt-14 flex flex-wrap gap-x-10 gap-y-4 border-t border-white/10 pt-6 text-sm text-stone-300">
            <Link to="/sectors/care-homes" className="hover:text-[#C59B27]" data-testid="hero-care-link">01 — Care Homes</Link>
            <Link to="/sectors/schools" className="hover:text-[#C59B27]" data-testid="hero-schools-link">02 — Schools</Link>
            <Link to="/sectors/corporate" className="hover:text-[#C59B27]" data-testid="hero-corporate-link">03 — Corporate</Link>
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5" style={{ perspective: 1200 }}>
          <motion.div
            style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
            className="relative mx-auto h-[440px] w-full max-w-[460px] sm:h-[560px] lg:h-[640px]"
          >
            <motion.div
              initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
              animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
              transition={{ duration: 1.4, delay: base + 0.2, ease: [0.76, 0, 0.24, 1] }}
              className="arch absolute inset-0 overflow-hidden ring-1 ring-[#C59B27]/40"
            >
              <motion.img
                style={{ y: imgY, scale: 1.18 }}
                src="/images/hero_chef_plating.jpg"
                alt="Chef finishing a plated dish"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#141412]/70 via-transparent to-transparent" />
            </motion.div>
            <motion.div {...fade(base + 1.1)} style={{ z: 60 }} className="absolute -left-2 bottom-16 rounded-2xl border border-white/15 bg-[#141412]/70 px-5 py-4 backdrop-blur-xl sm:-left-10">
              <p className="eyebrow text-[10px] text-[#C59B27]">Subscribe online</p>
              <p className="mt-1 font-display text-lg">Plans from £1,250/mo</p>
            </motion.div>
            <motion.div {...fade(base + 1.25)} style={{ z: 90 }} className="absolute -right-1 top-10 rounded-full bg-[#C59B27] px-5 py-3 text-sm font-semibold text-[#141412] sm:-right-6">
              Cooked fresh, daily
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
